// src/components/station/StationWorkshiftsInline.jsx
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Switch, Button
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import axios from '../../config/axiosInstance';
import { UserContext } from '../context/UserContext';
import { hasAnyRequiredPermission, WORKSHIFT_LOCAL_WRITE_PERMISSIONS, STAFFING_PAGE_PERMISSIONS } from '../../config/routePermissions';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

// anchos fijos para columnas "pinneadas"
const W = {
  id: 70,
  name: 220,
  horario: 120,
  tipo: 90,
  ambito: 90,
  principal: 100,
  estado: 110,
};

// left acumulado para sticky (izquierda)
const OFF = {
  id: 0,
  name: W.id,
  horario: W.id + W.name,
  tipo: W.id + W.name + W.horario,
  ambito: W.id + W.name + W.horario + W.tipo,
  principal: W.id + W.name + W.horario + W.tipo + W.ambito,
};

export default function StationWorkshiftsInline({ stationId, stationName }) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { permissions } = useContext(UserContext);
  const canRead = permissions.includes('workshifts.read') && permissions.includes('station_workareas.read');
  const canWrite =
    hasAnyRequiredPermission(permissions, WORKSHIFT_LOCAL_WRITE_PERMISSIONS) &&
    permissions.includes('station_workareas.write');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [showDisabled, setShowDisabled] = useState(true);

  const [quantities, setQuantities] = useState([]);
  const [stationWorkareas, setStationWorkareas] = useState([]);


  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [effRes, qtyRes, relRes] = await Promise.all([
        axios.get(`/station/${stationId}/workshifts?include_disabled=true`),
        axios.get(`/station/${stationId}/workshifts/quantities?include_disabled=true`),
        axios.get(`/station-workareas/station/${stationId}`),
      ]);
      setRows(effRes.data || []);
      setQuantities(qtyRes.data || []);
      setStationWorkareas(relRes.data || []);
    } catch (e) {
      console.error(e);
      setRows([]); setQuantities([]); setStationWorkareas([]);
    } finally {
      setLoading(false);
    }
  }, [stationId]);

  useEffect(() => {
    if (canRead) {
      fetchData();
    }
  }, [canRead, fetchData]);

  // Áreas (Mini/Pista), cada una con las posiciones (workarea) reales que
  // agrupa -- Mini agrupa Caja+Reponedor, Pista se agrupa sola. La matriz de
  // cobertura y la validación de encargado se muestran y editan a nivel área;
  // por atrás se sigue guardando por posición (workarea) para no tocar el
  // validador de cobertura ni la generación de calendario -- mismo patrón que
  // la fusión del calendario. El valor entero se escribe en la "posición
  // primaria" del área (la de menor id, hoy siempre Caja para Mini) y el
  // resto queda en 0, que es como ya está la data real hoy.
  const areas = useMemo(() => {
    const map = new Map();
    (quantities || []).forEach(q => {
      if (q.id_position == null) return;
      if (!map.has(q.id_position)) {
        map.set(q.id_position, {
          id: q.id_position,
          name: q.position_name || `Área ${q.id_position}`,
          workareaIds: [],
        });
      }
      const entry = map.get(q.id_position);
      if (!entry.workareaIds.includes(q.workarea_id)) entry.workareaIds.push(q.workarea_id);
    });
    return Array.from(map.values())
      .map(a => ({ ...a, workareaIds: [...a.workareaIds].sort((x, y) => x - y) }))
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [quantities]);

  const relationByArea = useMemo(() => {
    const map = new Map();
    (stationWorkareas || []).forEach(rel => {
      map.set(Number(rel.id_workarea), rel);
    });
    return map;
  }, [stationWorkareas]);

  const list = useMemo(() => {
    const filtered = showDisabled ? rows : rows.filter(r => r.enabled);
    return [...filtered].sort((a, b) => {
      if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
      if (a.is_principal !== b.is_principal) return a.is_principal ? -1 : 1;
      return String(a.hour_from || '').localeCompare(String(b.hour_from || ''));
    });
  }, [rows, showDisabled]);

  const toggleEnabled   = async (r) => { try { await axios.patch(`/station/${stationId}/workshifts/${r.id}?enabled=${!r.enabled}`); fetchData(); } catch { enqueueSnackbar('No se pudo cambiar el estado', { variant: 'error' }); } };
  const togglePrincipal = async (r) => { try { await axios.patch(`/station/${stationId}/workshifts/${r.id}?is_principal=${!r.is_principal}`); fetchData(); } catch { enqueueSnackbar('No se pudo marcar principal', { variant: 'error' }); } };

  const saveManagerControl = async (area, controlManager) => {
    try {
      await Promise.all(area.workareaIds.map((waId) => {
        const existing = relationByArea.get(Number(waId));
        const payload = {
          id_station: Number(stationId),
          id_workarea: Number(waId),
          control_manager: Boolean(controlManager),
        };
        return existing
          ? axios.put('/station-workareas', payload)
          : axios.post('/station-workareas', payload);
      }));
      setStationWorkareas((prev) => {
        const rest = (prev || []).filter((rel) => !area.workareaIds.includes(Number(rel.id_workarea)));
        const updated = area.workareaIds.map((waId) => ({
          id_station: Number(stationId),
          id_workarea: Number(waId),
          control_manager: Boolean(controlManager),
        }));
        return [...rest, ...updated];
      });
    } catch (e) {
      console.error(e);
      enqueueSnackbar('No se pudo guardar el control de encargado para esta área', { variant: 'error' });
    }
  };

  const fmt = v => (v || '').slice(0, 5);
  const fmtRange = r => (
    r.break_from && r.break_to
      ? `${fmt(r.hour_from)}–${fmt(r.break_from)} / ${fmt(r.break_to)}–${fmt(r.hour_to)}`
      : `${fmt(r.hour_from)}–${fmt(r.hour_to)}`
  );

  // estilos base sticky (mismo fondo para que no se “trasluzca”)
  const stickyTd = (left) => ({
    position: 'sticky', left, zIndex: 1, backgroundColor: 'background.paper',
    borderRight: '1px solid rgba(0,0,0,0.06)',
  });
  const stickyTh = (left) => ({
    position: 'sticky', left, zIndex: 2, backgroundColor: 'background.paper',
    borderRight: '1px solid rgba(0,0,0,0.12)',
    top: 0,
  });
  const stickyRightTd = {
    position: 'sticky', right: 0, zIndex: 1, backgroundColor: 'background.paper',
    borderLeft: '1px solid rgba(0,0,0,0.06)',
  };
  const stickyRightTh = {
    position: 'sticky', right: 0, zIndex: 2, backgroundColor: 'background.paper',
    borderLeft: '1px solid rgba(0,0,0,0.12)',
    top: 0,
  };

  if (!canRead) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="error">
          No tienes permiso para ver la configuración de turnos de esta estación.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 2, border: '1px solid #eee' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="subtitle1">Turnos de {stationName}</Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2">Mostrar deshabilitados</Typography>
            <Switch checked={showDisabled} onChange={e => setShowDisabled(e.target.checked)} />
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1}>
          {hasAnyRequiredPermission(permissions, STAFFING_PAGE_PERMISSIONS) && (
            <Button variant="outlined" onClick={() => navigate('/dotacion')}>
              Gestionar dotación
            </Button>
          )}
          <Button variant="outlined" onClick={fetchData} startIcon={<RestartAltIcon />}>
            Refrescar
          </Button>
        </Stack>
      </Stack>

      {areas.length > 0 && (
        <Box
          sx={{
            mb: 1.5,
            p: 1.5,
            bgcolor: '#fff',
            borderRadius: 1.5,
            border: '1px solid #eee',
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Validación de encargado por área
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
            Activa o desactiva la validación de presencia de encargado en cada área de esta estación.
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 1,
            }}
          >
            {areas.map((area) => {
              const primaryId = area.workareaIds[0];
              const checked = Boolean(relationByArea.get(Number(primaryId))?.control_manager);
              return (
                <Box
                  key={`manager-${area.id}`}
                  sx={{
                    px: 1.25,
                    py: 1,
                    border: '1px solid #E6EBF1',
                    borderRadius: 1.5,
                    bgcolor: checked ? '#F6FBF7' : '#FCFCFD',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={area.name}>
                        {area.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {checked ? 'Controlar presencia de encargado' : 'No validar encargado en esta área'}
                      </Typography>
                    </Box>
                    <Switch
                      size="small"
                      checked={checked}
                      onChange={(e) => saveManagerControl(area, e.target.checked)}
                      disabled={!canWrite}
                    />
                  </Stack>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* SCROLL HORIZONTAL */}
      <Box sx={{ overflowX: 'auto', borderRadius: 1, border: '1px solid #eee' }}>
        <Table size="small" sx={{ '& td, & th': { py: 0.5 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...stickyTh(OFF.id),   minWidth: W.id,      width: W.id }}>ID</TableCell>
              <TableCell sx={{ ...stickyTh(OFF.name), minWidth: W.name,    width: W.name }}>Nombre</TableCell>
              <TableCell sx={{ ...stickyTh(OFF.horario), minWidth: W.horario, width: W.horario }}>Horario</TableCell>
              <TableCell sx={{ ...stickyTh(OFF.tipo), minWidth: W.tipo,    width: W.tipo }}>Tipo</TableCell>
              <TableCell sx={{ ...stickyTh(OFF.ambito), minWidth: W.ambito,  width: W.ambito }}>Ámbito</TableCell>
              <TableCell sx={{ ...stickyTh(OFF.principal), minWidth: W.principal, width: W.principal }}>Principal</TableCell>


              <TableCell sx={{ ...stickyRightTh, minWidth: W.estado, width: W.estado }}>Estado</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {!loading && list.map(r => (
              <TableRow key={r.id}>
                <TableCell sx={{ ...stickyTd(OFF.id),   minWidth: W.id,      width: W.id,      color: r.enabled ? 'inherit' : '#999' }}>{r.id}</TableCell>

                <TableCell sx={{ ...stickyTd(OFF.name), minWidth: W.name,    width: W.name,    color: r.enabled ? 'inherit' : '#999', textDecoration: r.enabled ? 'none' : 'line-through' }}>
                  {r.name}
                </TableCell>

                <TableCell sx={{ ...stickyTd(OFF.horario), minWidth: W.horario, width: W.horario, color: r.enabled ? 'inherit' : '#999' }}>
                  {fmtRange(r)}
                </TableCell>

                <TableCell sx={{ ...stickyTd(OFF.tipo), minWidth: W.tipo, width: W.tipo }}>
                  {r.is_night ? <Chip size="small" label="Nocturno" /> : <Chip size="small" label="Diurno" variant="outlined" />}
                </TableCell>

                <TableCell sx={{ ...stickyTd(OFF.ambito), minWidth: W.ambito, width: W.ambito }}>
                  {r.is_global ? <Chip size="small" color="info" label="Global" /> : <Chip size="small" color="warning" label="Local" />}
                </TableCell>

                <TableCell sx={{ ...stickyTd(OFF.principal), minWidth: W.principal, width: W.principal }}>
                  <Switch checked={!!r.is_principal} onChange={() => togglePrincipal(r)} disabled={!canWrite} />
                </TableCell>


                <TableCell sx={{ ...stickyRightTd, minWidth: W.estado, width: W.estado }}>
                  <Switch checked={!!r.enabled} onChange={() => toggleEnabled(r)} disabled={!canWrite} />
                </TableCell>
              </TableRow>
            ))}

            {loading && (
              <TableRow><TableCell colSpan={7}>Cargando…</TableCell></TableRow>
            )}
            {!loading && list.length === 0 && (
              <TableRow><TableCell colSpan={7}>Sin turnos para mostrar.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      {areas.length === 0 && (
        <Typography variant="caption" color="text.secondary">
          No hay áreas de trabajo con cantidades para esta estación todavía.
        </Typography>
      )}
    </Box>
  );
}
